/**
 * MONSTER TOWN — HUD DOM HELPERS. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * Every interactive thing in the HUD is built here so the kid-usability rules
 * are structural rather than remembered:
 *
 *  - Minimum 88 CSS px, plus an invisible 10 px halo (see `.mt-btn::after`), so
 *    a finger that lands just outside still counts.
 *  - Press is committed on POINTERUP over the same control that received the
 *    pointerdown. A kid who presses and slides off has changed their mind, and
 *    nothing fires — a mis-tap is never punished.
 *  - No double-tap gesture exists anywhere in this file.
 *  - Every control makes a sound and moves. Silent, still buttons feel broken.
 *  - Icons only. `label` is an aria-label for accessibility, never rendered.
 */

import { bus } from '../core/events'
import { iconEl } from './icons'

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className = '',
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className !== '') node.className = className
  for (const c of children) node.append(c)
  return node
}

export interface TapOpts {
  /** Icon name from src/ui/icons.ts. */
  icon?: string
  /** Screen-reader / dev label. NEVER shown — LAW #4. */
  label: string
  className?: string
  iconSize?: number
  /** Cue played on press. Pass null for controls that make their own noise. */
  cue?: string | null
}

/**
 * Wire tap behaviour onto any element. Used for buttons, chips, cards, swatches
 * — anything a finger lands on.
 */
export function onTap(node: HTMLElement, fn: () => void, cue: string | null = 'ui_tap'): void {
  let armed = false
  node.addEventListener(
    'pointerdown',
    (e) => {
      // Do not stop propagation: the audio unlock listener on window must see
      // the very first gesture, even if it lands on a HUD button.
      e.preventDefault()
      armed = true
      node.dataset['press'] = '1'
      node.setPointerCapture?.(e.pointerId)
    },
    { passive: false },
  )
  const release = (): void => {
    armed = false
    delete node.dataset['press']
  }
  node.addEventListener('pointerup', (e) => {
    e.preventDefault()
    e.stopPropagation()
    const wasArmed = armed
    release()
    if (!wasArmed) return
    if (node.hasAttribute('disabled')) return
    if (cue !== null) bus.emit('audio:cue', { cue })
    fn()
  })
  node.addEventListener('pointercancel', release)
  node.addEventListener('pointerleave', release)
  // Desktop convenience only. Touch never reaches this.
  node.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    if (node.hasAttribute('disabled')) return
    if (cue !== null) bus.emit('audio:cue', { cue })
    fn()
  })
}

/** The one button factory. Nothing in the HUD builds a <button> by hand. */
export function button(opts: TapOpts, fn: () => void): HTMLButtonElement {
  const b = el('button', `mt-btn ${opts.className ?? ''}`.trim())
  b.type = 'button'
  b.setAttribute('aria-label', opts.label)
  b.title = opts.label
  if (opts.icon !== undefined) b.append(iconEl(opts.icon, opts.iconSize ?? 52))
  onTap(b, fn, opts.cue === undefined ? 'ui_tap' : opts.cue)
  return b
}

/** Swap a button's icon without rebuilding it (mode toggle, speaker states). */
export function setIcon(node: HTMLElement, icon: string, sizePx: number): void {
  const old = node.querySelector('.mt-ico')
  const next = iconEl(icon, sizePx)
  if (old === null) node.append(next)
  else old.replaceWith(next)
}

/** A row of counting dots. Numbers a 3-year-old can count without reading. */
export function dots(total: number, done = 0): HTMLElement {
  const row = el('span', 'mt-dots')
  for (let i = 0; i < Math.min(total, 8); i++) {
    const d = el('span', 'mt-dot')
    d.dataset['done'] = String(i < done)
    row.append(d)
  }
  return row
}

/** Remove every child. Cheaper and safer than innerHTML = ''. */
export function clear(node: HTMLElement): void {
  while (node.firstChild !== null) node.removeChild(node.firstChild)
}
